<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue'
import ShakeDetector from './shakeDetector'
import { useStaticAsset } from '@/composables/useStaticAsset'

const asset = useStaticAsset()

const emit = defineEmits<{ (e: 'cancel'): void }>()

const audio = ref<HTMLAudioElement | null>(null)
const button = ref<HTMLButtonElement | null>(null)
const container = ref<HTMLElement | null>(null)
const web = ref<HTMLElement | null>(null)

const shakeAvailable = ref(false)
const showNotification = ref(false)

let iterationTimeout: ReturnType<typeof setTimeout> | null = null
let eyesAnimInterval: ReturnType<typeof setInterval> | null = null
let shakeDetector: ShakeDetector | null = null
let cancelled = false
let audioPauseHandler: (() => void) | null = null

let cancelMoveListener: (() => void) | null = null
let cancelWebListener: (() => void) | null = null

const triggerCancel = () => {
  emit('cancel')
  cancelMoveListener?.()
  cancelWebListener?.()
}

const getOrientedRect = (element: Element) => {
  const rect = element.getBoundingClientRect()
  const isPortrait = window.matchMedia('(orientation: portrait)').matches

  if (!isPortrait) return rect

  return {
    left: rect.top,
    top: window.innerWidth - rect.right,
    width: rect.width,
    height: rect.height
  }
}

const onResize = () => {
  if (!audio.value || !button.value || !container.value) return

  const buttonRect = getOrientedRect(button.value)
  container.value.style.left = `${buttonRect.left - 36}px`
}

const setWebPosition = () => {
  if (!button.value || !web.value) return

  const buttonRect = getOrientedRect(button.value)
  if ((web.value as HTMLImageElement).hidden) return

  web.value.style.left = `${buttonRect.left + buttonRect.width / 2 - web.value.offsetWidth / 2}px`
  web.value.style.top = `${buttonRect.top + buttonRect.height / 2 - web.value.offsetHeight / 2}px`
}

const moveSpiderBackUp = () => {
  if (!container.value) return

  const containerRect = getOrientedRect(container.value)
  container.value.animate(
    [{ top: `${containerRect.top}px` }, { top: `${containerRect.top + 32}px` }, { top: '-150px' }],
    {
      duration: 500,
      easing: 'ease-in-out',
      fill: 'forwards'
    }
  )

  showNotification.value = false
}

const onMoveCancel = () => {
  cancelled = true

  container.value?.classList.remove('student-v2-audio-spider_no-swing')
  if (eyesAnimInterval) clearInterval(eyesAnimInterval)
  moveSpiderBackUp()
}

const moveSpider = (iteration = 0) => {
  if (!audio.value || !button.value || !container.value) return

  const buttonRect = getOrientedRect(button.value)
  const containerRect = getOrientedRect(container.value)

  const totalIterations = 5
  if (cancelled) {
    cancelled = false
    return
  }

  if (iteration === 0) {
    container.value.animate(
      [
        { top: `${containerRect.top}px`, transformOrigin: `center -${Math.abs(containerRect.top)}px` },
        { top: '24px', transformOrigin: 'center -24px' }
      ],
      {
        duration: 300,
        easing: 'ease-in-out',
        fill: 'forwards'
      }
    )
  } else {
    const vertMoveAmount = (buttonRect.top + 24) / totalIterations - 1
    const newPositionY = vertMoveAmount * iteration + 1
    const intermediatePosY = newPositionY + 24

    container.value.animate(
      [
        { top: `${containerRect.top}px`, transformOrigin: `center -${Math.abs(containerRect.top)}px` },
        { top: `${intermediatePosY}px`, transformOrigin: `center -${Math.abs(intermediatePosY)}px` },
        { top: `${newPositionY}px`, transformOrigin: `center -${Math.abs(newPositionY)}px` }
      ],
      {
        duration: 750,
        easing: 'ease-in',
        fill: 'forwards'
      }
    )
  }

  if (iteration < totalIterations - 1) {
    iterationTimeout = setTimeout(
      () => {
        moveSpider(iteration + 1)
      },
      8000 / (totalIterations - 1)
    )
    return
  }

  if (eyesAnimInterval) clearInterval(eyesAnimInterval)

  const spiderTransform = window.getComputedStyle(container.value).transform

  container.value.animate([{ transform: spiderTransform }, { transform: 'rotate(0deg)' }], {
    duration: 300,
    easing: 'ease-in-out'
  }).onfinish = () => {
    if (!container.value) return
    container.value.style.transform = 'none'
  }

  container.value.classList.add('student-v2-audio-spider_no-swing')
  setTimeout(() => makeWeb(), 500)
}

const makeWeb = () => {
  cancelMoveListener = null

  if (!container.value || !web.value) return
  const webEl = web.value

  const image1 = container.value.querySelector('.student-v2-audio-spider__image_1-1') as HTMLImageElement | null
  const image2 = container.value.querySelector('.student-v2-audio-spider__image_1-2') as HTMLImageElement | null
  const image3 = container.value.querySelector('.student-v2-audio-spider__image_2') as HTMLImageElement | null
  if (!image1 || !image2 || !image3) return

  image1.hidden = false
  image2.hidden = true
  image3.hidden = true

  const legsAnimInterval = setInterval(() => {
    image1.hidden = !image1.hidden
    image3.hidden = !image3.hidden
  }, 200)

  const webAppear = setTimeout(() => {
    clearInterval(legsAnimInterval)

    image1.hidden = true
    image2.hidden = false
    image3.hidden = true
    webEl.hidden = false
    setWebPosition()

    const webAppearAnim = webEl.animate([{ opacity: 0 }, { opacity: 1 }], {
      duration: 300,
      easing: 'ease-in-out'
    })

    webAppearAnim.onfinish = () => {
      webAppearAnim.commitStyles()
      audio.value?.pause()
      cancelWebListener = null
    }
  }, 1500)

  const goBack = setTimeout(() => {
    moveSpiderBackUp()
  }, 2000)

  cancelWebListener = () => {
    clearTimeout(webAppear)
    clearTimeout(goBack)
    clearInterval(legsAnimInterval)

    image1.hidden = false
    image2.hidden = true
    image3.hidden = true

    moveSpiderBackUp()
    cancelWebListener = null
  }
}

const removeWeb = () => {
  if (!web.value) return

  const webDisappearAnim = web.value.animate([{ opacity: 1 }, { opacity: 0 }], {
    duration: 300,
    easing: 'ease-in-out'
  })
  webDisappearAnim.onfinish = () => {
    ;(web.value as HTMLImageElement).hidden = true
  }
}

const $start = (playerContainer: HTMLElement) => {
  audio.value = playerContainer.querySelector('audio') as HTMLAudioElement | null
  button.value = playerContainer.querySelector(
    'button.student-v2-audio-player__playpause-btn'
  ) as HTMLButtonElement | null
  if (!audio.value || !button.value || !container.value || !web.value) return

  if (audioPauseHandler) {
    audio.value.removeEventListener('pause', audioPauseHandler)
  }
  audioPauseHandler = () => triggerCancel()
  audio.value.addEventListener('pause', audioPauseHandler)

  if (iterationTimeout) clearTimeout(iterationTimeout)
  container.value.classList.remove('student-v2-audio-spider_no-swing')
  ;(web.value as HTMLImageElement).hidden = true

  cancelled = false
  onResize()
  moveSpider()
  cancelMoveListener = onMoveCancel
  cancelWebListener = null

  if (eyesAnimInterval) clearInterval(eyesAnimInterval)
  eyesAnimInterval = setInterval(() => {
    if (!container.value) return

    const image1 = container.value.querySelector('.student-v2-audio-spider__image_1-1') as HTMLImageElement | null
    const image2 = container.value.querySelector('.student-v2-audio-spider__image_1-2') as HTMLImageElement | null
    if (!image1 || !image2) return

    image1.hidden = !image1.hidden
    image2.hidden = !image2.hidden
  }, 2000)

  showNotification.value = true
}

const $onPageChange = () => {
  setWebPosition()
  onResize()
}

defineExpose({ $start, $onPageChange })

void (async () => {
  shakeAvailable.value = !!ShakeDetector.hasPermission && (await ShakeDetector.hasAccelerometer())

  if (!shakeAvailable.value) return

  shakeDetector = new ShakeDetector({
    onShake: () => {
      if (!audio.value || !showNotification.value) return
      triggerCancel()
    }
  })
  shakeDetector.start()
})()

onMounted(() => {
  window.addEventListener('resize', onResize)
  onResize()
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', onResize)
  if (audio.value && audioPauseHandler) {
    audio.value.removeEventListener('pause', audioPauseHandler)
  }
  if (iterationTimeout) clearTimeout(iterationTimeout)
  if (eyesAnimInterval) clearInterval(eyesAnimInterval)
  shakeDetector?.stop()
})
</script>

<template>
  <div>
    <div ref="container" class="student-v2-audio-spider" @click="triggerCancel">
      <img
        class="student-v2-audio-spider__image student-v2-audio-spider__image_1-1"
        :src="asset('img/student-v2/spider/spider_1_1.svg')"
      />
      <img
        hidden
        class="student-v2-audio-spider__image student-v2-audio-spider__image_1-2"
        :src="asset('img/student-v2/spider/spider_1_2.svg')"
      />
      <img
        hidden
        class="student-v2-audio-spider__image student-v2-audio-spider__image_2"
        :src="asset('img/student-v2/spider/spider_2.svg')"
      />
    </div>

    <img
      ref="web"
      hidden
      class="student-v2-audio-spider__web"
      :src="asset('img/student-v2/spider/spider_web.svg')"
      @click="removeWeb"
    />

    <div
      class="student-v2-audio-spider__notification"
      :class="{ 'student-v2-audio-spider__notification_hidden': !showNotification }"
      @click="removeWeb"
    >
      <p class="text-title-bold mb-1">Ты еще здесь?</p>
      {{ shakeAvailable ? 'Встряхни свое устройство, чтобы продолжить!' : 'Нажми на паучка, чтобы продолжить!' }}
    </div>
  </div>
</template>

<style lang="scss">
@use '~/styles/pages/student2/screen' as s2;

.student-v2-audio-spider {
  position: fixed;
  top: -150px;
  z-index: 10;
  max-width: unset !important;
  width: unset !important;
  cursor: pointer;

  animation: swing 2s ease-in-out infinite;
  &_no-swing {
    animation: none;
  }

  &::before {
    content: ' ';
    position: absolute;
    left: calc(50% - 2px);
    width: 4px;
    height: 100vh;
    background-color: black;
    bottom: 50px;
    z-index: -1;
    filter: blur(0.75px);
    pointer-events: none;
  }

  &__image {
    width: 110px;
    @include s2.mobile {
      width: 86px;
    }
  }

  &__web {
    height: 96px;
    position: fixed;
    z-index: 9;
  }

  @keyframes swing {
    0%,
    100% {
      transform: rotate(-3deg);
    }
    50% {
      transform: rotate(3deg);
    }
  }

  &__notification {
    position: fixed;
    z-index: 10;
    cursor: pointer;

    top: 24px;
    right: 24px;
    max-width: min(300px, 60vw);
    background-color: white;
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.2);
    border-radius: 8px;
    padding: 16px;

    &_hidden {
      transform: translateY(-150%);
      opacity: 0;
      visibility: hidden;
    }

    transition:
      transform 0.3s ease-out,
      opacity 0.3s ease-out,
      visibility 0.3s ease-out;
  }
}
</style>
